import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { createPostUseCase, getUserTournamentsUseCase } from '@/src/core/dependencies';
import { logger } from '@/src/core/utils';
import { Tournament, ModerationError } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { useImageUpload } from '@/src/presentation/hooks/useImageUpload';
import { CreatePostScreen } from '@/src/presentation/screens/CreatePostScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';

export default function CreatePostContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);

  const resetForm = useCallback(() => {
    setContent('');
    setImageUrl(undefined);
    setSelectedTournamentId(undefined);
  }, []);

  const { pickAndUploadImage, uploading } = useImageUpload({
    bucket: 'post-images',
    userId: user?.id || '',
    preset: 'post',
    fieldType: 'post_image',
    onSuccess: (url) => {
      setImageUrl(url);
    },
    onError: (error) => {
      logger.error('CreatePost', 'Image upload failed', { error });
    },
  });

  useEffect(() => {
    loadUserTournaments();
  }, [user]);

  // Reset form when screen receives focus
  useFocusEffect(
    useCallback(() => {
      resetForm();
    }, [resetForm])
  );

  const loadUserTournaments = async () => {
    if (!user) return;

    try {
      const data = await getUserTournamentsUseCase.execute(user.id, 100, 0);
      setTournaments(data);
    } catch (error) {
      logger.error('CreatePost', 'Failed to load tournaments', { error });
      // Continue even if loading tournaments fails
      setTournaments([]);
    }
  };

  const handleCreatePost = async () => {
    if (!user) {
      platformAlert(t('common.error'), t('alerts.mustBeLoggedIn'));
      return;
    }

    if (!content.trim()) {
      platformAlert(t('common.error'), t('alerts.postContentRequired'));
      return;
    }

    if (content.length > 500) {
      platformAlert(t('common.error'), t('alerts.maxCharactersExceeded'));
      return;
    }

    let postCreated = false;
    try {
      setCreating(true);
      await createPostUseCase.execute(user.id, content.trim(), imageUrl, selectedTournamentId);
      resetForm();
      postCreated = true;
      platformAlert(t('common.success'), t('alerts.postCreated'));
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('CreatePost', 'Failed to create post', { error });
        platformAlert(t('common.error'), t('feed.failedToPost'));
      }
    } finally {
      setCreating(false);
    }

    // Navigate outside try-catch — router.back() can throw on web when there
    // is no navigation history (Drawer navigator does not maintain a stack).
    // router.replace is safe on all platforms and navigates explicitly to feed.
    if (postCreated) {
      router.replace('/feed' as any);
    }
  };

  const handleCancel = () => {
    if (content.trim() || imageUrl) {
      platformAlert(t('alerts.discardPost'), t('alerts.discardPostConfirm'), [
        { text: t('alerts.keep'), style: 'cancel' },
        {
          text: t('alerts.discard'),
          style: 'destructive',
          onPress: () => router.replace('/feed' as any),
        },
      ]);
    } else {
      router.replace('/feed' as any);
    }
  };

  const handleImageRemove = () => {
    setImageUrl(undefined);
  };

  return (
    <CreatePostScreen
      content={content}
      onContentChange={setContent}
      imageUrl={imageUrl}
      onImagePick={pickAndUploadImage}
      onImageRemove={handleImageRemove}
      uploading={uploading}
      tournaments={tournaments}
      selectedTournamentId={selectedTournamentId}
      onTournamentSelect={setSelectedTournamentId}
      onCreatePost={handleCreatePost}
      onCancel={handleCancel}
      creating={creating}
      colors={colors}
    />
  );
}
